import React from 'react';
import { Link } from 'react-router-dom';
import { 
  FiGlobe, FiArrowRight, FiUsers, FiSettings, FiDollarSign,
  FiBarChart2, FiShield, FiCheckCircle
} from 'react-icons/fi';
import PublicPageHeader from '../../../components/common/PublicPageHeader';
import PublicPageFooter from '../../../components/common/PublicPageFooter';
import DocsSidebar from '../../../components/docs/DocsSidebar';

const AdminOrgAdmin = () => {
  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      <PublicPageHeader />
      
      <div className="flex">
        <DocsSidebar currentPath="/docs/admin/org-admin" />
        
        <main className="flex-1 max-w-4xl mx-auto px-6 py-12">
          {/* Breadcrumb */}
          <nav className="flex items-center space-x-2 text-sm text-gray-500 dark:text-gray-400 mb-8">
            <Link to="/docs" className="hover:text-blue-600">Docs</Link>
            <span>/</span>
            <Link to="/docs/admin" className="hover:text-blue-600">Admin & Permissions</Link>
            <span>/</span>
            <span className="text-gray-900 dark:text-white">Organization Admin Guide</span>
          </nav>

          {/* Header */}
          <div className="mb-12">
            <div className="inline-flex p-2 bg-green-100 dark:bg-green-900/30 rounded-xl mb-4">
              <FiGlobe className="w-6 h-6 text-green-600" />
            </div>
            <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">Organization Administration</h1>
            <p className="text-xl text-gray-600 dark:text-gray-300">
              Complete guide for Organization Admins managing their Tasklina organization.
            </p>
          </div>

          {/* Overview */}
          <section className="mb-16">
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-2xl p-8">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Organization Admin Role</h2>
              <p className="text-gray-600 dark:text-gray-300 mb-6">
                As an Organization Admin, you have full control over your organization's settings, 
                users, departments, and billing. You're responsible for maintaining the structure 
                and security of your workspace.
              </p>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-white dark:bg-gray-800 rounded-xl p-4">
                  <h4 className="font-medium text-gray-900 dark:text-white mb-2">Your Responsibilities</h4>
                  <ul className="text-sm text-gray-600 dark:text-gray-300 space-y-1">
                    <li>• Organization configuration</li>
                    <li>• User and role management</li>
                    <li>• Department structure</li>
                    <li>• Billing and subscriptions</li>
                  </ul>
                </div>
                <div className="bg-white dark:bg-gray-800 rounded-xl p-4">
                  <h4 className="font-medium text-gray-900 dark:text-white mb-2">Quick Access</h4>
                  <ul className="text-sm text-gray-600 dark:text-gray-300 space-y-1">
                    <li>• Organization Settings → General</li>
                    <li>• Organization Settings → Users</li>
                    <li>• Organization Settings → Billing</li>
                    <li>• Organization Settings → Security</li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* Key Capabilities */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Key Capabilities</h2>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-4">
                <FiUsers className="w-6 h-6 text-blue-600 mb-2" />
                <h3 className="font-semibold text-gray-900 dark:text-white mb-1">User Management</h3>
                <p className="text-sm text-gray-600 dark:text-gray-300">Invite, manage, and remove organization members</p>
              </div>
              <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-4">
                <FiSettings className="w-6 h-6 text-purple-600 mb-2" />
                <h3 className="font-semibold text-gray-900 dark:text-white mb-1">Organization Settings</h3>
                <p className="text-sm text-gray-600 dark:text-gray-300">Configure organization profile and preferences</p>
              </div>
              <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-4">
                <FiDollarSign className="w-6 h-6 text-green-600 mb-2" />
                <h3 className="font-semibold text-gray-900 dark:text-white mb-1">Billing Management</h3>
                <p className="text-sm text-gray-600 dark:text-gray-300">Manage subscription and payment settings</p>
              </div>
              <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-4">
                <FiBarChart2 className="w-6 h-6 text-orange-600 mb-2" />
                <h3 className="font-semibold text-gray-900 dark:text-white mb-1">Analytics Access</h3>
                <p className="text-sm text-gray-600 dark:text-gray-300">View organization-wide analytics and reports</p>
              </div>
            </div>
          </section>

          {/* Common Tasks */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Common Admin Tasks</h2>
            <div className="space-y-4">
              <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-4">
                <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Setting Up Departments</h3>
                <ol className="text-sm text-gray-600 dark:text-gray-300 space-y-1 list-decimal list-inside">
                  <li>Go to Organization Settings → Departments</li>
                  <li>Click "Create Department"</li>
                  <li>Enter department name and assign a manager</li>
                  <li>Configure department budget (optional)</li>
                </ol>
              </div>
              <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-4">
                <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Inviting Users</h3>
                <ol className="text-sm text-gray-600 dark:text-gray-300 space-y-1 list-decimal list-inside">
                  <li>Go to Organization Settings → Users</li>
                  <li>Click "Invite User"</li>
                  <li>Enter email and select role</li>
                  <li>Assign to department</li>
                </ol>
              </div>
              <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-4">
                <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Managing Billing</h3>
                <ol className="text-sm text-gray-600 dark:text-gray-300 space-y-1 list-decimal list-inside">
                  <li>Go to Organization Settings → Billing</li>
                  <li>View current plan and usage</li>
                  <li>Add/remove seats as needed</li>
                  <li>Update payment method</li>
                </ol>
              </div>
            </div>
          </section>

          {/* Best Practices */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Best Practices</h2>
            <div className="bg-gray-50 dark:bg-gray-800 rounded-2xl p-6">
              <ul className="space-y-3">
                <li className="flex items-start space-x-3">
                  <FiCheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700 dark:text-gray-200">Follow the principle of least privilege when assigning roles</span>
                </li>
                <li className="flex items-start space-x-3">
                  <FiCheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700 dark:text-gray-200">Regularly review user access and deactivate unused accounts</span>
                </li>
                <li className="flex items-start space-x-3">
                  <FiCheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700 dark:text-gray-200">Enable two-factor authentication for enhanced security</span>
                </li>
                <li className="flex items-start space-x-3">
                  <FiCheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700 dark:text-gray-200">Monitor analytics to understand organization activity</span>
                </li>
              </ul>
            </div>
          </section>

          {/* Navigation */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Related Guides</h2>
            <div className="grid md:grid-cols-2 gap-4">
              <Link
                to="/docs/admin/roles"
                className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-xl hover:bg-blue-50 dark:bg-blue-900/30 transition-all"
              >
                <span className="font-medium text-gray-900 dark:text-white">Role Hierarchy</span>
                <FiArrowRight className="text-gray-400 dark:text-gray-500" />
              </Link>
              <Link
                to="/docs/admin/security"
                className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-xl hover:bg-blue-50 dark:bg-blue-900/30 transition-all"
              >
                <span className="font-medium text-gray-900 dark:text-white">Security Settings</span>
                <FiArrowRight className="text-gray-400 dark:text-gray-500" />
              </Link>
            </div>
          </section>

          {/* Footer */}
          <div className="border-t border-gray-200 dark:border-gray-700 pt-8">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-500 dark:text-gray-400">Last updated: January 27, 2026</div>
              <div className="flex items-center space-x-4">
                <span className="text-sm text-gray-500 dark:text-gray-400">Was this helpful?</span>
                <button className="px-3 py-1 text-sm bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700">Yes</button>
                <button className="px-3 py-1 text-sm bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700">No</button>
              </div>
            </div>
          </div>
        </main>
      </div>

      <PublicPageFooter />
    </div>
  );
};

export default AdminOrgAdmin;
